import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  Paper,
  TextField,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import { getExportProductionDataByPaging } from "../../../services/Export";
import TableItem from "../../../components/TableItem";

// Icons
import VisibilityIcon from "@mui/icons-material/Visibility";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import FactoryIcon from "@mui/icons-material/Factory";
import FilterListIcon from "@mui/icons-material/FilterList";

const useStyles = makeStyles({
  container: {
    padding: "20px",
  },
});

function Ex_ProductionList() {
  const theme = useTheme();
  const classes = useStyles();
  const history = useHistory();

  // State
  const [productions, setProductions] = useState([]);
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(0);

  // Filter state
  const [filters, setFilters] = useState({
    _barcode: "",
    _productName: "",
  });

  const [appliedFilters, setAppliedFilters] = useState({
    _barcode: "",
    _productName: "",
  });

  const columns = [
    {
      id: "productionDate",
      label: "Production Date",
      minWidth: 150,
      align: "left",
    },
    {
      id: "productName",
      label: "Product Name",
      minWidth: 180,
      align: "left",
    },
    {
      id: "barcode",
      label: "Barcode",
      minWidth: 120,
      align: "left",
    },
    {
      id: "productionQty",
      label: "Production Qty",
      minWidth: 120,
      align: "right",
    },
    {
      id: "totalCost",
      label: "Total Cost",
      minWidth: 120,
      align: "right",
    },
    {
      id: "bomVersion",
      label: "BOM Version",
      minWidth: 120,
      align: "center",
    },
    {
      id: "addedBy",
      label: "Added By",
      minWidth: 150,
      align: "left",
    },
    {
      id: "status",
      label: "Status",
      minWidth: 140,
      align: "left",
    },
    {
      id: "action",
      label: "Actions",
      minWidth: 100,
      align: "center",
    },
  ];

  const createData = (
    productionId,
    productionDate,
    productName,
    barcode,
    productionQty,
    totalCost,
    bomVersion,
    useStandardBOM,
    addedBy,
    status,
  ) => {
    return {
      productionId,
      productionDate,
      productName,
      barcode,
      productionQty,
      totalCost,
      bomVersion,
      useStandardBOM,
      addedBy,
      status,
      action: productionId,
    };
  };

  // "AWAITING_APPROVAL" -> "Awaiting Approval"
  const formatStatus = (status) =>
    status
      ? status
          .replace(/_/g, " ")
          .toLowerCase()
          .replace(/\b\w/g, (c) => c.toUpperCase())
      : "N/A";

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const result = await getExportProductionDataByPaging(
        page,
        pageSize,
        appliedFilters,
      );

      if (result && result.content) {
        setProductions(result.content);
        setTotalPages(result.totalPages);

        // Transform data to table rows
        const tableRows = result.content.map((production) =>
          createData(
            production.productionId,
            formatDate(production.productionDate),
            production.productName || "N/A",
            production.productBarcode || "N/A",
            production.productionQty,
            formatCurrency(production.totalCost),
            production.bomVersion,
            production.useStandardBOM,
            production.addedBy,
            formatStatus(production.status),
          ),
        );

        setRows(tableRows);
      }
    } catch (error) {
      console.error("Error fetching productions:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [page, pageSize, appliedFilters]);

  const handleFilterChange = (name, value) => {
    setFilters({
      ...filters,
      [name]: value,
    });
  };

  const handleApplyFilters = () => {
    setAppliedFilters(filters);
    setPage(0); // Reset to first page when applying filters
  };

  const handleClearFilters = () => {
    const clearedFilters = {
      _barcode: "",
      _productName: "",
    };
    setFilters(clearedFilters);
    setAppliedFilters(clearedFilters);
    setPage(0);
  };

    const handleAction = (event, rowId) => {
      console.log("Action triggered:", event, rowId);
    if (event === "view") {
      history.push(`/template/ex_production_view/${rowId}`);
    }
  };

  const formatCurrency = (value) => {
    return typeof value === "number"
      ? `Rs.${value.toFixed(2)}`
      : `Rs.${parseFloat(value || 0).toFixed(2)}`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  return (
    <Paper className={classes.container} elevation={8}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <FactoryIcon fontSize="large" color="primary" />
          <Box>
            <Typography variant="h5" fontWeight="600" color="primary.main">
              Production Records
            </Typography>
            <Typography variant="caption" color="text.secondary">
              View and manage all production entries
            </Typography>
          </Box>
        </Box>
      </Box>

      <Divider sx={{ mb: 3 }} />

      {/* Filters */}
      <Card elevation={1} sx={{ mb: 3, borderRadius: 2 }}>
        <CardContent>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              mb: 2,
              gap: 1,
            }}
          >
            <FilterListIcon color="primary" />
            <Typography
              variant="subtitle1"
              fontWeight="600"
              color="primary.main"
            >
              Filters
            </Typography>
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                size="small"
                label="Product Barcode"
                placeholder="Search by barcode..."
                value={filters._barcode}
                onChange={(e) => handleFilterChange("_barcode", e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === "Enter") handleApplyFilters();
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                size="small"
                label="Product Name"
                placeholder="Search by product name..."
                value={filters._productName}
                onChange={(e) =>
                  handleFilterChange("_productName", e.target.value)
                }
                onKeyPress={(e) => {
                  if (e.key === "Enter") handleApplyFilters();
                }}
              />
            </Grid>
            <Grid item xs={12} sm={12} md={4}>
              <Box sx={{ display: "flex", gap: 1, height: "100%" }}>
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<SearchIcon />}
                  onClick={handleApplyFilters}
                >
                  Search
                </Button>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<ClearIcon />}
                  onClick={handleClearFilters}
                >
                  Clear
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Productions Table */}
      {isLoading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "400px",
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <TableItem
          columns={columns}
          rows={rows}
          page={page}
          setPage={setPage}
          tablePagin={true}
          totalPages={totalPages}
          getData={fetchData}
          handleAction={handleAction}
          showActions={{
            view: "View Details",
          }}
          actionRoles={{
            view: ["PRODUCTION_VIEW", "ROLE_ADMIN"],
          }}
          customRenderers={{
            productionQty: (row) => (
              <Chip label={row.productionQty} size="small" color="primary" />
            ),
            barcode: (row) => (
              <Chip label={row.barcode} size="small" variant="outlined" />
            ),
            bomVersion: (row) => (
              <Box sx={{ display: "flex", justifyContent: "center" }}>
                {row.useStandardBOM ? (
                  <Chip
                    label={`BOM v${row.bomVersion || 1}`}
                    size="small"
                    color="success"
                    variant="outlined"
                  />
                ) : (
                  <Chip
                    label="Manual"
                    size="small"
                    color="warning"
                    variant="outlined"
                  />
                )}
              </Box>
            ),
            addedBy: (row) => (
              <Typography variant="caption" color="text.secondary">
                {row.addedBy}
              </Typography>
            ),
          }}
        />
      )}
    </Paper>
  );
}

export default Ex_ProductionList;
